import { jsx as _jsx } from "react/jsx-runtime";
import { SERIES_TONES } from "./manifest.js";
import { resolveLabel } from "./derive.js";
import { Cell, figureWords, formatCell, numbersIn, useNarrowScreen } from "./render.js";
import { BarList, TrendChart } from "./chart.js";
/**
 * A card drawn as a chart: `bars` — a bar per row named by `by`, against the largest `figure` — or
 * `trend` — `figure` (or each of `series`, up to four) along `by`. The same on a hub and on a record
 * page, so one draw says one thing wherever it is attached (D-4). Values are written by their
 * columns; a name with a `ref` is a link to its record. Nothing to show — no rows, or series with
 * nothing in them — is the card's empty words, never a flat line that reads as a measured zero. An
 * empty value is a gap in its line. Today is the row the server marked (`today`), never the
 * renderer's clock.
 */
/** `2025-10` as the reader names the month: «أكتوبر» under the axis, «أكتوبر 2025» on hover. */
export function monthWords(value, ctx, year) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}$/.test(value))
        return null;
    return new Date(`${value}-01T12:00:00Z`).toLocaleDateString(numbersIn(ctx.i18n.locale), { month: 'long', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' });
}
const measured = (v) => {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean')
        return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const marked = (v) => v === true || v === 'true' || v === 1;
/** The lines a trend card draws from its rows: each series a column, or the one `figure`. */
export function trendSeries(manifest, rows, ctx) {
    const panel = manifest.panel;
    const columns = manifest.columns ?? [];
    const by = columns.find((c) => c.key === panel.by);
    if (!by)
        return [];
    const asked = panel.series?.length ? panel.series : panel.figure ? [{ key: panel.figure, tone: (panel.tone ?? 'blue') }] : [];
    return asked.flatMap((line, n) => {
        const column = columns.find((c) => c.key === line.key);
        if (!column)
            return [];
        const label = 'label' in line || 'label_key' in line ? resolveLabel(line.label ?? column.label, line.label_key ?? column.label_key, ctx.i18n) : resolveLabel(column.label, column.label_key, ctx.i18n);
        const points = rows.map((r, i) => {
            const value = measured(r[column.key]);
            return {
                key: String(r[by.key] ?? i),
                label: monthWords(r[by.key], ctx, false) ?? formatCell(r[by.key], by, ctx.i18n),
                title: monthWords(r[by.key], ctx, true) ?? formatCell(r[by.key], by, ctx.i18n),
                value,
                text: value === null ? '—' : figureWords(r[column.key], column, r, columns, ctx),
            };
        });
        return [{ key: column.key, label, tone: line.tone ?? SERIES_TONES[n % SERIES_TONES.length], points }];
    });
}
export function ChartPanel({ manifest, rows, empty, caption, ctx }) {
    const narrow = useNarrowScreen();
    const panel = manifest.panel;
    const columns = manifest.columns ?? [];
    const by = columns.find((c) => c.key === panel.by);
    if (!by)
        return empty;
    if (panel.draw === 'trend') {
        const series = trendSeries(manifest, rows, ctx);
        const values = series.flatMap((l) => l.points.map((p) => p.value)).filter((v) => v !== null);
        if (!values.length || values.every((v) => v === 0))
            return empty;
        const first = columns.find((c) => c.key === series[0].key);
        const todayRow = panel.today ? rows.findIndex((r) => marked(r[panel.today])) : -1;
        return (_jsx(TrendChart, { series: series, caption: caption, rtl: ctx.i18n.dir === 'rtl', width: narrow ? 340 : 640, "data-part-chart": "trend", ...(todayRow >= 0 ? { today: series[0].points[todayRow].key, todayLabel: ctx.i18n.t('chart.today') } : {}), format: (n) => formatCell(n, { ...first, signed: false }, ctx.i18n) }));
    }
    const figure = columns.find((c) => c.key === panel.figure);
    if (!figure)
        return empty;
    const tone = (panel.tone ?? 'blue');
    const valued = rows.filter((r) => measured(r[figure.key]) !== null);
    if (!valued.length || valued.every((r) => Number(r[figure.key]) === 0))
        return empty;
    return (_jsx(BarList, { tone: tone, caption: caption, "data-part-chart": "bars", items: valued.slice(0, panel.limit ?? 10).map((r, i) => ({
            key: String(r['id'] ?? r[by.key] ?? i),
            label: _jsx(Cell, { value: r[by.key], column: by, ctx: ctx, row: r }),
            title: formatCell(r[by.key], by, ctx.i18n),
            value: Number(r[figure.key]),
            text: figureWords(r[figure.key], figure, r, columns, ctx),
        })) }));
}
//# sourceMappingURL=chart-panel.js.map